import { createContext, useContext, useCallback, useEffect, useRef, useState, lazy, Suspense } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { IncomingCall } from "@/components/calls/IncomingCall";

// The Stream Video SDK is heavy — only fetched when a call actually starts.
const CallScreen = lazy(() => import("@/components/calls/CallScreen").then((m) => ({ default: m.CallScreen })));

const CallContext = createContext({ startCall: () => {}, inCall: false });
export const useCall = () => useContext(CallContext);

export function CallProvider({ children }) {
  const { user, token } = useAuth();
  const { subscribe } = useRealtime();
  const [call, setCall] = useState(null); // active call object
  const [role, setRole] = useState(null); // 'caller' | 'callee'
  const [peer, setPeer] = useState(null); // peer user object (for the on-screen identity)
  const [phase, setPhase] = useState("idle"); // idle | incoming | in-call
  const dismissed = useRef(new Set()); // call ids we've finished with
  const callIdRef = useRef(null);
  useEffect(() => { callIdRef.current = call?.id || null; }, [call?.id]);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.activeCall();
      const c = res?.call || res;
      if (!c || !c.id) return;
      if (dismissed.current.has(c.id)) return;
      setPhase((currentPhase) => {
        if (currentPhase !== "idle") return currentPhase; // already handling a call
        if (c.status === "ringing" && c.callee_id === user.id) {
          setCall(c); setRole("callee"); setPeer(c.caller || null);
          api.ackCall(c.id).catch(() => {});
          return "incoming";
        }
        return currentPhase;
      });
    } catch { /* transient */ }
  }, [user?.id]);

  // Poll for incoming calls (baseline) — WS events nudge an immediate refresh.
  useEffect(() => {
    if (!token || !user?.id) return undefined;
    refresh();
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, [token, user?.id, refresh]);

  // Canonical realtime call events (v1.3.4). Poll above stays as a safety net (zombie sockets drop frames).
  useEffect(() => subscribe((evt) => {
    const t = evt?.type;
    if (!t || !String(t).startsWith("call")) return;
    // call.ring → show the incoming screen instantly from the pushed payload.
    if (t === "call.ring") {
      const c = evt.call;
      if (c?.id && c.callee_id === user?.id && !dismissed.current.has(c.id)) {
        setPhase((p) => {
          if (p !== "idle") return p;
          setCall(c); setRole("callee"); setPeer(c.caller || null);
          api.ackCall(c.id).catch(() => {});
          return "incoming";
        });
      }
      return;
    }
    // Peer ended/declined → tear down immediately if it's our current call.
    if (t === "call.ended" || t === "call.declined") {
      const cid = evt.call_id || evt.call?.id;
      if (cid) {
        dismissed.current.add(cid);
        if (callIdRef.current === cid) reset();
      }
      return;
    }
    // cc_9: symmetric E2EE downgrade — web is already plaintext (transport-encrypted), nothing to tear down.
    if (t === "call.e2ee_off") return;
    // call.acked / call.accepted / anything else → reconcile via the API.
    refresh();
  }), [subscribe, refresh, user?.id]);

  const startCall = useCallback(async (chatId, opts = {}) => {
    if (phase !== "idle") return;
    try {
      const res = await api.createCall(chatId, { video: !!opts.video });
      const c = res?.call || res;
      if (!c?.id) throw new Error("Call could not be started.");
      dismissed.current.delete(c.id);
      setCall(c); setRole("caller"); setPeer(opts.peer || c.callee || null); setPhase("in-call");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d) || d?.message || e?.message || "Couldn't start the call.");
    }
  }, [phase]);

  const reset = () => { setPhase("idle"); setCall(null); setRole(null); setPeer(null); };

  const acceptIncoming = async () => {
    try { await api.acceptCall(call.id); setPhase("in-call"); }
    catch { toast.error("Couldn't accept the call."); }
  };
  const declineIncoming = async () => {
    if (call?.id) { dismissed.current.add(call.id); api.declineCall(call.id).catch(() => {}); }
    reset();
  };
  const endActive = () => {
    if (call?.id) dismissed.current.add(call.id);
    reset();
  };

  return (
    <CallContext.Provider value={{ startCall, inCall: phase === "in-call" }}>
      {children}
      {phase === "incoming" && call && (
        <IncomingCall call={call} onAccept={acceptIncoming} onDecline={declineIncoming} />
      )}
      {phase === "in-call" && call && (
        <Suspense fallback={null}>
          <CallScreen callId={call.id} video={!!call.video} peer={peer} role={role} onEnd={endActive} />
        </Suspense>
      )}
    </CallContext.Provider>
  );
}
